/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, inject, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {UIRouterModule} from '@uirouter/angular';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import * as _ from 'lodash';
import * as moment from 'moment';
import template from './segment-edit-page.component.html';
import {AdnSelectSearchElement} from '../common/directives/adn-select/adn-select.component';
import {AdnSelectComponent} from '../common/directives/adn-select/adn-select.component';
import {APP_SHARED} from '../common/app-shared';
import {FORM_CONTROLS} from '../common/directives/form-controls/form-controls';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {TTL} from './select-field-constants';
import {FolderApiService} from '../folder/folder-api.service';
import {SearchApiService} from '../common/controller/search-api.service';
import {SegmentApiService, SegmentModel} from './segment-api.service';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, UIRouterModule, ButtonsModule, AdnSelectComponent, ...FORM_CONTROLS],
  selector: 'adn-segment-edit-page',
  template
})
export class SegmentEditPageComponent extends SignalAdminResourceEditPageBase<SegmentModel> {
  readonly TTL = TTL;
  readonly ttlUnits = ['Minutes', 'Hours', 'Days'];

  private readonly segmentResultsState = signal<any[]>([]);
  private readonly allFoldersState = signal<any[] | null>(null);
  get segmentResults(): any[] { return this.segmentResultsState(); }
  set segmentResults(value: any[]) { this.segmentResultsState.set(value); }
  get allFolders(): any[] | null { return this.allFoldersState(); }
  set allFolders(value: any[] | null) { this.allFoldersState.set(value); }

  constructor(
    private readonly folderApi: FolderApiService,
    private readonly searchApi: SearchApiService
  ) {
    super(inject(SegmentApiService), 'segment');

    this.folderApi.listOptions().subscribe((page) => {
      this.allFolders = page.results || [];
    });
  }

  protected override initialiseModel(model: SegmentModel): SegmentModel {
    const editingModel = super.initialiseModel(model);
    editingModel.dataSource = editingModel.dataSource || 'ADNUNTIUS';

    if (!editingModel.timeToLive) {
      editingModel.ttlUnit = 'Days';
      editingModel.ttlSpec = false;
    } else {
      const inDays = moment.duration(editingModel.timeToLive).asDays();
      const inHours = moment.duration(editingModel.timeToLive).asHours();
      const inMinutes = moment.duration(editingModel.timeToLive).asMinutes();
      editingModel.ttlSpec = true;

      if (inDays % 1 === 0) {
        editingModel.ttlNumber = inDays;
        editingModel.ttlUnit = 'Days';
      } else if (inHours % 1 === 0) {
        editingModel.ttlNumber = inHours;
        editingModel.ttlUnit = 'Hours';
      } else {
        editingModel.ttlNumber = inMinutes;
        editingModel.ttlUnit = 'Minutes';
      }
    }

    if (this.isNew && editingModel.global === undefined) {
      editingModel.global = true;
    }
    editingModel.folders = _.map(editingModel.folders, (folder: any) => {
      return _.isObject(folder) ? (folder as any).id : folder;
    });
    return editingModel;
  }

  protected override prepareForSubmit(model: SegmentModel): void {
    model.timeToLive = model.ttlSpec
      ? 'P' + (model.ttlUnit !== 'Days' ? 'T' : '') + model.ttlNumber + model.ttlUnit.charAt(0)
      : null;
  }

  segSearch = (searchElement: AdnSelectSearchElement): void => {
    this.searchApi.search({
      q: searchElement.search,
      types: 'UserSegment',
      objectState: 'ACTIVE',
      dataSource: 'ADNUNTIUS'
    }).subscribe((data) => {
      this.segmentResults = data.searchResults || [];
    });
  };

  onFolderChecklistChanged(): void {
    this.markExternalFormDirty();
  }
}
